import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { ArrowRight } from 'lucide-react';

const FigureDrawingLessonsPage = () => {
    return (
        <div data-testid="figure-drawing-lessons-page">
            <SEO
                title="Figure Drawing Lessons Online"
                description="Study figure drawing from master artists online. Learn anatomy, gesture, proportion, and classical figure construction with Glen Orbik, Steve Cefalo, and other professional instructors at Zarolla."
                url="/figure-drawing-lessons"
                keywords={['figure drawing lessons', 'figure drawing online', 'anatomy for artists', 'gesture drawing', 'classical figure drawing']}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'WebPage',
                    '@id': 'https://zarolla.com/figure-drawing-lessons#webpage',
                    url: 'https://zarolla.com/figure-drawing-lessons',
                    name: 'Figure Drawing Lessons Online | Zarolla Online Art Academy',
                    description: 'Study figure drawing from master artists online. Learn anatomy, gesture, proportion, and classical figure construction.',
                    isPartOf: { '@id': 'https://zarolla.com/#website' },
                    datePublished: '2007-01-01',
                    dateModified: new Date().toISOString().split('T')[0],
                    inLanguage: 'en-US',
                }}
            />

            <div className="section-gap">
                <div className="container-main">
                    {/* Hero */}
                    <div className="max-w-3xl mb-16">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            The foundation of representational art
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight mt-4">
                            Figure drawing lessons from master artists.
                        </h1>
                        <p className="text-muted-foreground text-lg mt-6 max-w-2xl">
                            The human figure is the most complex and rewarding subject in art. Zarolla's figure drawing instruction comes from professional artists who have spent their careers studying and teaching anatomy, gesture, proportion, and the classical approach to the figure.
                        </p>
                    </div>

                    {/* Section 1 */}
                    <section className="py-12 border-t border-border">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                            <div>
                                <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                    Why figure drawing matters
                                </h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Figure drawing is the foundation of classical art training. Every great painter in the Western tradition — from Michelangelo to Sargent — built their abilities on a thorough understanding of the human form. The skills developed through figure study — proportion, gesture, anatomy, value — transfer directly to portrait painting, illustration, sculpture, and composition.
                                </p>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Our figure drawing instruction covers the complete skill set: quick gesture sketches that capture movement and energy, longer studies that develop accuracy and sensitivity to form, and anatomical knowledge that lets you draw convincingly even from imagination.
                                </p>
                                <p className="text-muted-foreground leading-relaxed">
                                    Instructors like Glen Orbik bring the Frank Reilly method's systematic approach to the figure, making complex construction accessible to students at every level.
                                </p>
                            </div>
                            <div className="bg-secondary/30 rounded-sm p-8">
                                <h3 className="font-medium text-primary mb-4">What you'll study:</h3>
                                <ul className="space-y-3 text-muted-foreground">
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Gesture and movement of the figure</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Proportional systems and measurement</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Anatomy for the artist — bones and muscles</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Light and shadow on the figure</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Constructive figure drawing methods</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Section 2 */}
                    <section className="py-12 border-t border-border">
                        <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                            From drawing to painting the figure
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div>
                                <h3 className="font-medium text-primary mb-3">Gesture and rhythm</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Before detail, before anatomy — there is gesture. Learning to capture the essential action and energy of a pose in seconds is where figure drawing begins, and it remains the most important skill throughout your development.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-medium text-primary mb-3">Structure and construction</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Systematic approaches to figure construction — including the Reilly method taught by Glen Orbik — give you reliable tools for building the figure from any angle. This structural understanding is what separates drawing from copying.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-medium text-primary mb-3">Into paint</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Strong drawing translates directly into confident painting. Our workshop videos show how instructors like Jeremy Lipking and Tony Pro carry their figure drawing skills into oil painting — from value block-in to finished work.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Section 3: CTA */}
                    <section className="py-12 border-t border-border">
                        <div className="max-w-2xl">
                            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                Start your figure drawing study
                            </h2>
                            <p className="text-muted-foreground leading-relaxed mb-8">
                                Browse our courses to find figure drawing and anatomy instruction at your level. Pair course study with our workshop videos to see how professional artists apply figure drawing skills in their painting practice.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Link to="/courses">
                                        Browse Courses <ArrowRight className="ml-2 h-4 w-4" />
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/sculptures">Explore Sculpture Videos</Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/about">Meet the Instructors</Link>
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default FigureDrawingLessonsPage;
